import { DestroyRef, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { ConfigurationService } from './configuration.service';
import { CreateCompanyDto } from '../interfaces/dto/create-company.dto';
import { BehaviorSubject, Observable } from 'rxjs';
import { UpdateCompanyDataDto } from '../interfaces/dto/update-company-data.dto';
import { UpdateCompanyAddressDto } from '../interfaces/dto/update-company-address.dto';
import { UpdateCompanyContactDto } from '../interfaces/dto/update-company-contact.dto';
import { AvailableSiret } from '../interfaces/domain-interface';
import { CompanyResult } from '../interfaces/company-interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnyValue } from '@shared/types/any-value.type';
import { HANDLE_ERRORS_IN_COMPONENT } from '../http-context-tokens/handle-errors-in-component.http-context-token';

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  COMPANY_ROUTE_API = 'api/v2.0/companies';
  COMPANY_UPDATE_DATA_ROUTE_API = 'api/v2.0/companies/{domainId}/data';
  CURRENT_COMPANY_CODE_STORAGE = 'current_company_code';
  CURRENT_COMPANY_KEY_STORAGE = 'current_company_id';

  public currentCompany$: BehaviorSubject<CompanyResult | null> = new BehaviorSubject<CompanyResult | null>(null);
  public getCurrentCompany(): Observable<CompanyResult | null> {
    return this.currentCompany$.asObservable();
  }

  public constructor(
    private readonly httpClient: HttpClient,
    private readonly configService: ConfigurationService,
    private readonly destroyRef: DestroyRef,
  ) {}

  public createCompany(company: CreateCompanyDto): Observable<AnyValue> {
    return this.httpClient.post(`${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}`, company);
  }

  public updateCompanyDataCustomer(
    data: UpdateCompanyDataDto,
    domainId: string,
    handleErrorsInComponent?: boolean,
  ): Observable<AnyValue> {
    return this.httpClient.put(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/${domainId}/data`,
      data,
      {
        context: new HttpContext().set(HANDLE_ERRORS_IN_COMPONENT, !!handleErrorsInComponent),
      },
    );
  }

  public updateCompanyAddress(data: UpdateCompanyAddressDto, domainId: string): Observable<AnyValue> {
    return this.httpClient.put(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/${domainId}/address`,
      data,
    );
  }

  public updateCompanyContact(data: UpdateCompanyContactDto, domainId: string): Observable<AnyValue> {
    return this.httpClient.put(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/${domainId}/contact`,
      data,
    );
  }

  public availableSiret(siret: string): Observable<AvailableSiret> {
    return this.httpClient.get<AvailableSiret>(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/IsAvailableSiret/${siret}`,
    );
  }

  public getCompany(domainCode: number): Observable<CompanyResult> {
    return this.httpClient.get<CompanyResult>(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/${domainCode}`,
    );
  }

  public changeCurrentCompany(codeDomain: number) {
    this.getCompany(codeDomain)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((company: CompanyResult) => {
        sessionStorage.removeItem('PartnerCodeFC');
        this.currentCompany$.next(company);
        sessionStorage.setItem(this.CURRENT_COMPANY_KEY_STORAGE, company.idDomaine ?? '');
        sessionStorage.setItem(this.CURRENT_COMPANY_CODE_STORAGE, company.domainCode ?? '');

        if (company.products.some((dp) => dp.partnerCode != null))
          sessionStorage.setItem(
            'PartnerCodeFC',
            company.products.find((dp) => dp.partnerCode !== null)?.partnerCode ?? '',
          );
      });
  }

  public clearLocalStorageCompany() {
    sessionStorage.removeItem(this.CURRENT_COMPANY_KEY_STORAGE);
    sessionStorage.removeItem(this.CURRENT_COMPANY_CODE_STORAGE);
  }

  public getPartnerIsExpert(partnerCode: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${this.configService.urlApiSiVision}/${this.COMPANY_ROUTE_API}/partner/isExpert/${partnerCode}`,
    );
  }

  public finalizeSubscription(orderId: string): Observable<AnyValue> {
    return this.httpClient.post<AnyValue>(
      `${this.configService.urlApiSiVision}/v2.0/transversals/partner/waiting-domains/${orderId}`,
      {},
    );
  }
}
